/**
 * MiraPlanView · /table agentic search-bar result surface.
 *
 * Founder direction 9 Feb 2026: "world's most sophisticated agentic
 * concierge backed reservation global system." When the member types
 * a conversational query into the search bar ("dinner for 4 bangalore
 * chinese · vegan · wheelchair"), the search bar calls /api/table/
 * agent/plan and this view renders the agent's understanding +
 * ranked candidates + planned routes.
 *
 * Route ladder (lower tier = better):
 *   Tier 0  · booking_override                 (desk handpicked)
 *   Tier 1  · anchor_url                       (lc_archive anchor)
 *   Tier 1B · archive_overlay                  (overlay booking_url)
 *   Tier 2  · sevenrooms_probe                 (auto-discovered)
 *   Tier 3  · google_reserve / opentable /
 *            resy / tock / thefork / chope     (live programmatic)
 *   Tier 4  · concierge_dispatch               (desk only · no link)
 *
 * Auto-execute rule (option c, founder approved): for Tier 0-3 the
 * card surfaces a "Reserve now" deeplink. For Tier 4 the only action
 * is "Send to Concierge®" which writes a table_concierge_dispatches
 * row the desk picks up. Both actions optionally accept member notes.
 */
import React, { useState } from "react";
import { agentDispatch } from "@/lib/table-api";
import { UnderstandingChipRow } from "@/components/concierge/UnderstandingChip";


function tierClass(tier) {
    if (tier <= 1.5) return "tier-anchor";
    if (tier <= 2) return "tier-probe";
    if (tier < 4) return "tier-live";
    return "tier-concierge";
}


function RouteBadge({ route, auto_execute }) {
    if (!route) return null;
    const t = route.tier ?? 4;
    const dot = t <= 1.5 ? "var(--t-brass)"
        : t <= 2 ? "var(--t-accent, #2f7a3e)"
            : t < 4 ? "var(--t-ink2)"
                : "var(--t-ink3)";
    return (
        <span
            data-testid="mira-route-badge"
            className={tierClass(t)}
            style={{
                fontSize: 10,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "var(--t-ink2)",
                background: "var(--t-paper2)",
                border: "1px solid var(--t-line2)",
                borderRadius: 999,
                padding: "3px 9px",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
            }}
        >
            <span
                aria-hidden
                style={{
                    width: 6, height: 6, borderRadius: 999,
                    background: dot, display: "inline-block",
                }}
            />
            {route.platform.replace(/_/g, " ")} · {route.confidence}
        </span>
    );
}


function CandidateCard({ cand, planEnvelope, onSelect, onDispatched }) {
    const [dispatching, setDispatching] = useState(false);
    const [dispatched, setDispatched] = useState(null); // dispatch_id
    const [showNotes, setShowNotes] = useState(false);
    const [notes, setNotes] = useState("");

    const rp = cand.route_plan || {};
    const primary = rp.primary || null;
    const isAutoExec = Boolean(rp.auto_execute) && primary?.url;
    const tier = primary?.tier ?? 4;

    const doDispatch = async () => {
        if (dispatching || dispatched) return;
        setDispatching(true);
        try {
            const r = await agentDispatch({
                request_id: planEnvelope.request_id,
                chosen_place_id: cand.place_id,
                plan_envelope: planEnvelope,
                notes: notes.trim() || null,
            });
            setDispatched(r.dispatch_id);
            onDispatched?.(r);
        } catch (e) {
            // soft-fail · the dispatch row endpoint enforces 201
            alert(`Could not send to Concierge®: ${e?.message || e}`);
        } finally {
            setDispatching(false);
        }
    };

    return (
        <div
            data-testid={`mira-candidate-${cand.place_id}`}
            style={{
                background: "var(--t-card)",
                border: "1px solid var(--t-line2)",
                borderRadius: 14,
                marginBottom: 12,
                cursor: "default",
                overflow: "hidden",
            }}
        >
            {/* ── Hero photo · founder regression fix 9 Feb 2026 PM ──
               Restores parity with RestaurantCard.jsx · same 150px
               hero, same lazy-load + onError hide, same overlay
               gradient · so a free-text query rendered here looks
               like the rich photo card it did before commit b86999a
               stripped this surface. */}
            {cand.photo_url ? (
                <div
                    className="relative overflow-hidden"
                    style={{ height: 150, background: "var(--t-paper2)" }}
                    onClick={() => onSelect?.(cand)}
                    role="button"
                >
                    <img
                        src={cand.photo_url}
                        alt={cand.name}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        data-testid={`mira-candidate-photo-${cand.place_id}`}
                    />
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background:
                                "linear-gradient(to top, rgba(33,28,21,0.45) 0%, rgba(33,28,21,0) 55%)",
                        }}
                    />
                </div>
            ) : null}

            <div style={{ padding: "16px 18px" }}>
            <div className="flex items-start justify-between gap-3">
                <div
                    style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                    onClick={() => onSelect?.(cand)}
                    data-testid={`mira-candidate-open-${cand.place_id}`}
                >
                    <div className="flex items-start justify-between gap-3 mb-1">
                        <div className="flex items-center gap-2">
                            <span
                                className="t-mono uppercase"
                                style={{
                                    fontSize: 10, letterSpacing: "0.18em",
                                    color: "var(--t-ink3)", fontWeight: 600,
                                }}
                            >
                                rank {(cand.rank_score * 100).toFixed(0)}
                            </span>
                            <RouteBadge route={primary} auto_execute={isAutoExec} />
                        </div>
                        {/* Rating + reviews count · founder regression fix 9 Feb
                           2026 PM #2 · "Held in regard by N diners" lives in
                           the whisper line below, but the at-a-glance ★ 4.8 ·
                           (2.1k) belongs in the header. Mirrors the same
                           rendering in RestaurantCard.jsx so the agent-plan
                           card and the browse card never diverge in tone. */}
                        {cand.rating ? (
                            <span
                                className="flex items-baseline gap-1 shrink-0"
                                data-testid={`mira-candidate-rating-${cand.place_id}`}
                                title={cand.user_ratings_total
                                    ? `${cand.user_ratings_total.toLocaleString()} guest ratings`
                                    : "Diner rating"}
                            >
                                <span style={{ fontSize: 12, color: "var(--t-ink2)" }}>
                                    ★ {Number(cand.rating).toFixed(1)}
                                </span>
                                {cand.user_ratings_total ? (
                                    <span
                                        className="t-mono"
                                        style={{
                                            fontSize: 10,
                                            color: "var(--t-ink3)",
                                            letterSpacing: "0.04em",
                                        }}
                                    >
                                        ({cand.user_ratings_total >= 1000
                                            ? `${(cand.user_ratings_total / 1000).toFixed(cand.user_ratings_total >= 10000 ? 0 : 1)}k`
                                            : cand.user_ratings_total.toLocaleString()})
                                    </span>
                                ) : null}
                            </span>
                        ) : null}
                    </div>
                    <h3
                        className="t-serif"
                        style={{
                            fontSize: 20, fontWeight: 500, margin: "2px 0 6px",
                            lineHeight: 1.15, letterSpacing: "-0.005em",
                        }}
                    >
                        {cand.name}
                    </h3>
                    {/* Cuisine · city · country line · founder regression
                       fix 9 Feb 2026 PM #2 · mirrors the location line in
                       RestaurantCard so the agent card carries the same
                       at-a-glance geography signal. */}
                    {(cand.tags?.length || cand.city || cand.country) ? (
                        <p
                            data-testid={`mira-candidate-location-${cand.place_id}`}
                            style={{
                                fontSize: 12.5, color: "var(--t-ink2)",
                                marginTop: 0, marginBottom: 4,
                            }}
                        >
                            {Array.isArray(cand.tags) && cand.tags.length > 0
                                ? `${cand.tags[0]} · `
                                : ""}
                            {[cand.city, cand.country].filter(Boolean).join(", ")}
                        </p>
                    ) : null}
                    {cand.address ? (
                        <p style={{ fontSize: 12, color: "var(--t-ink3)", margin: 0 }}>
                            {cand.address}
                        </p>
                    ) : null}
                    {/* Concierge whisper · founder regression fix 9 Feb
                       2026 PM #2 · the italic preview line ("Held in
                       regard by X diners on Google") that turns a bare
                       listing into a felt recommendation. Same source
                       and same wording as RestaurantCard.jsx · the
                       two cards must speak with one voice. */}
                    <p
                        style={{
                            fontSize: 13, color: "var(--t-ink2)",
                            lineHeight: 1.5, marginTop: 9,
                            fontStyle: "italic",
                        }}
                        data-testid={`mira-candidate-whisper-${cand.place_id}`}
                    >
                        {cand.user_ratings_total
                            ? `Held in regard by ${cand.user_ratings_total.toLocaleString()} diners.`
                            : "Verified on the live map."}
                    </p>
                    {Array.isArray(cand.rank_reasons) && cand.rank_reasons.length > 0 ? (
                        <ul
                            data-testid={`mira-candidate-reasons-${cand.place_id}`}
                            style={{
                                margin: "10px 0 0", padding: 0, listStyle: "none",
                                display: "flex", flexWrap: "wrap", gap: 6,
                            }}
                        >
                            {cand.rank_reasons.slice(0, 4).map((r, i) => (
                                <li
                                    key={i}
                                    style={{
                                        fontSize: 11, color: "var(--t-ink2)",
                                        background: "var(--t-paper2)",
                                        border: "1px solid var(--t-line2)",
                                        borderRadius: 999, padding: "3px 9px",
                                    }}
                                >
                                    {r}
                                </li>
                            ))}
                        </ul>
                    ) : null}

                    {/* Understanding chip row · the trust contract.
                       Founder direction 9 Feb 2026: a member always
                       knows whether the AI heard them. ✓ matched =
                       attested by source. ⚠ unmet = honest hedge +
                       Concierge® will hand-confirm. */}
                    <UnderstandingChipRow
                        match={cand.understanding_match}
                        testIdPrefix={`mira-understanding-${cand.place_id}`}
                    />

                    {/* ── Atmosphere + dietary chip row · founder
                       regression fix 9 Feb 2026 PM · restores the
                       texture strips stripped by commit b86999a.
                       Source rendered exactly as the drawer renders
                       them (per Concierge · desk confirms at booking)
                       so the agent-plan card and the drawer never
                       drift in claim or label. ── */}
                    {Array.isArray(cand.atmosphere_chips) && cand.atmosphere_chips.length > 0 ? (
                        <ul
                            data-testid={`mira-candidate-atmosphere-${cand.place_id}`}
                            style={{
                                margin: "10px 0 0", padding: 0, listStyle: "none",
                                display: "flex", flexWrap: "wrap", gap: 6,
                            }}
                        >
                            {cand.atmosphere_chips.slice(0, 8).map((chip, i) => {
                                const isDietary = chip.group === "dietary";
                                const isAccess = chip.group === "accessibility";
                                return (
                                    <li
                                        key={`${chip.label}-${i}`}
                                        title={`${chip.label} · ${chip.value} · ${chip.source}`}
                                        data-testid={`mira-chip-${chip.group}-${i}-${cand.place_id}`}
                                        style={{
                                            fontSize: 11,
                                            color: isDietary
                                                ? "var(--t-accent, #2f7a3e)"
                                                : "var(--t-ink2)",
                                            background: isAccess
                                                ? "var(--t-paper2)"
                                                : "transparent",
                                            border: "1px solid var(--t-line2)",
                                            borderRadius: 999,
                                            padding: "3px 9px",
                                        }}
                                    >
                                        {chip.label}
                                    </li>
                                );
                            })}
                        </ul>
                    ) : null}
                </div>
            </div>

            {/* Action row · Tier 0-3 = deeplink + dispatch backup ·
               Tier 4 = dispatch only */}
            <div
                className="flex items-center gap-2 mt-4 pt-3"
                style={{ borderTop: "1px solid var(--t-line2)" }}
            >
                {dispatched ? (
                    <span
                        data-testid={`mira-dispatched-${cand.place_id}`}
                        style={{
                            fontSize: 12, color: "var(--t-accent, #2f7a3e)",
                            fontWeight: 600,
                        }}
                    >
                        ✓ Sent to Concierge® · #{dispatched.slice(0, 6)}
                    </span>
                ) : (
                    <>
                        {isAutoExec ? (
                            <a
                                href={primary.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`mira-reserve-now-${cand.place_id}`}
                                style={{
                                    fontSize: 12, fontWeight: 600,
                                    padding: "8px 16px", borderRadius: 999,
                                    background: "var(--t-ink)",
                                    color: "var(--t-paper)",
                                    textDecoration: "none",
                                    fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                                    letterSpacing: "0.02em",
                                }}
                            >
                                Reserve now →
                            </a>
                        ) : null}
                        <button
                            type="button"
                            onClick={doDispatch}
                            disabled={dispatching}
                            data-testid={`mira-dispatch-${cand.place_id}`}
                            style={{
                                fontSize: 12, fontWeight: 500,
                                padding: "8px 14px", borderRadius: 999,
                                background: isAutoExec
                                    ? "transparent"
                                    : "var(--t-ink)",
                                color: isAutoExec
                                    ? "var(--t-ink)"
                                    : "var(--t-paper)",
                                border: `1px solid ${
                                    isAutoExec ? "var(--t-line2)" : "var(--t-ink)"
                                }`,
                                cursor: dispatching ? "wait" : "pointer",
                                opacity: dispatching ? 0.6 : 1,
                                fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                            }}
                        >
                            {dispatching ? "Sending…" : "Send to Concierge®"}
                        </button>
                        {!showNotes ? (
                            <button
                                type="button"
                                onClick={() => setShowNotes(true)}
                                data-testid={`mira-add-notes-${cand.place_id}`}
                                style={{
                                    fontSize: 11, color: "var(--t-ink3)",
                                    background: "transparent",
                                    border: 0, cursor: "pointer",
                                    padding: "8px 6px",
                                    textDecoration: "underline",
                                    textUnderlineOffset: 3,
                                }}
                            >
                                + add note
                            </button>
                        ) : null}
                    </>
                )}
            </div>

            {showNotes && !dispatched ? (
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder="Special asks · e.g. window table, no peanuts, anniversary cake"
                    data-testid={`mira-notes-${cand.place_id}`}
                    className="mt-3 w-full px-3 py-2"
                    style={{
                        fontSize: 12, fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                        background: "var(--t-paper2)",
                        border: "1px solid var(--t-line2)",
                        borderRadius: 10, color: "var(--t-ink)",
                        outline: "none", resize: "vertical",
                    }}
                />
            ) : null}
            </div>{/* /padding wrapper · founder regression fix 9 Feb 2026 PM */}
        </div>
    );
}


export function MiraPlanView({ envelope, onSelectCandidate, onDispatched, onBackToHome }) {
    if (!envelope) return null;
    const u = envelope.understanding || {};
    const cands = envelope.candidates || [];
    const overflow = envelope.candidates_overflow || [];
    const noResults = cands.length === 0;

    return (
        <div
            data-testid="mira-plan-view"
            style={{
                maxWidth: 880, marginInline: "auto",
                paddingInline: 24, paddingTop: 18, paddingBottom: 60,
            }}
        >
            {/* Understanding strip — Mira's interpretation of what
               the member asked for. Member sees this and either
               accepts or refines their query. */}
            <div
                data-testid="mira-understanding"
                style={{
                    background: "var(--t-paper2)",
                    border: "1px solid var(--t-line2)",
                    borderRadius: 14,
                    padding: "14px 18px",
                    marginBottom: 18,
                }}
            >
                <div
                    className="t-mono uppercase"
                    style={{
                        fontSize: 9, letterSpacing: "0.28em",
                        color: "var(--t-brass)", fontWeight: 600,
                        marginBottom: 6,
                    }}
                >
                    Mira heard ↓
                </div>
                <div
                    className="t-serif"
                    style={{
                        fontSize: 22, lineHeight: 1.15,
                        letterSpacing: "-0.01em", fontWeight: 400,
                    }}
                >
                    {u.intent_summary || "Restaurant table"}
                </div>
                <div
                    className="mt-2 flex flex-wrap gap-2"
                    style={{ fontSize: 11, color: "var(--t-ink3)" }}
                >
                    {u.party_size ? <span>· {u.party_size} guests</span> : null}
                    {u.meal ? <span>· {u.meal}</span> : null}
                    {u.when_hint ? <span>· {u.when_hint}</span> : null}
                    {Array.isArray(u.dietary) && u.dietary.length > 0
                        ? <span>· {u.dietary.join("/")}</span> : null}
                    {Array.isArray(u.accessibility) && u.accessibility.length > 0
                        ? <span>· wheelchair</span> : null}
                </div>
            </div>

            {noResults ? (
                <div
                    data-testid="mira-no-results"
                    style={{
                        background: "var(--t-paper2)",
                        border: "1px solid var(--t-line2)",
                        borderRadius: 14,
                        padding: "32px 24px",
                        textAlign: "center",
                    }}
                >
                    <p className="t-serif" style={{ fontSize: 22, marginBottom: 8 }}>
                        Mira didn&apos;t find a verified match.
                    </p>
                    <p style={{ fontSize: 13, color: "var(--t-ink2)", marginBottom: 16 }}>
                        Send this to the Concierge® desk and we&apos;ll
                        hand-source the table for you.
                    </p>
                    {onBackToHome ? (
                        <button
                            type="button"
                            onClick={onBackToHome}
                            style={{
                                fontSize: 12, padding: "8px 16px",
                                borderRadius: 999,
                                background: "var(--t-ink)", color: "var(--t-paper)",
                                border: 0, cursor: "pointer",
                            }}
                        >
                            Try a different search
                        </button>
                    ) : null}
                </div>
            ) : (
                <div data-testid="mira-candidates">
                    {cands.map((c) => (
                        <CandidateCard
                            key={c.place_id}
                            cand={c}
                            planEnvelope={envelope}
                            onSelect={onSelectCandidate}
                            onDispatched={onDispatched}
                        />
                    ))}
                    {overflow.length > 0 ? (
                        <details style={{ marginTop: 18 }}>
                            <summary
                                style={{
                                    fontSize: 11, color: "var(--t-ink3)",
                                    cursor: "pointer", letterSpacing: "0.16em",
                                    textTransform: "uppercase",
                                    fontFamily: "'JetBrains Mono', monospace",
                                }}
                                data-testid="mira-overflow-toggle"
                            >
                                show {overflow.length} more
                            </summary>
                            <div style={{ marginTop: 12 }}>
                                {overflow.map((c) => (
                                    <CandidateCard
                                        key={c.place_id}
                                        cand={c}
                                        planEnvelope={envelope}
                                        onSelect={onSelectCandidate}
                                        onDispatched={onDispatched}
                                    />
                                ))}
                            </div>
                        </details>
                    ) : null}
                </div>
            )}

            {/* Doctrine footer · transparent about how Mira routes. */}
            <p
                data-testid="mira-doctrine-note"
                style={{
                    fontSize: 10, color: "var(--t-ink3)",
                    marginTop: 30, lineHeight: 1.5, fontStyle: "italic",
                    textAlign: "center", opacity: 0.7,
                }}
            >
                {envelope.doctrine_note}
            </p>
        </div>
    );
}
